import React, { useMemo } from "react";
import { Tooltip } from "@pd/pd-ui-kit";
import { StatusOption } from "@pd/pd-ui-kit/dist/Select/Select";
import { Control, Controller } from "react-hook-form";

import { Select as SelectPD } from "~/shared/ui/Select";
import {
  ConfigItem,
  FormValues,
} from "~/widgets/Filters/FilterSummary/schema/types";

import styles from "~/widgets/Filters/FilterSummary/FilterSummary.less";

export interface SelectIProps {
  fieldName: string;
  fieldConfig: Omit<ConfigItem, "fields">;
  control: Control<FormValues>;
  setFastItem: (prop: "day" | "week" | "month" | null) => void;
}

const Select = ({
  fieldName,
  fieldConfig,
  control,
  setFastItem,
}: SelectIProps) => {
  return (
    <div className={styles[fieldName]}>
      <span>{fieldConfig.label}</span>
      <Controller
        name={fieldName as keyof FormValues}
        control={control}
        render={({ onChange, value }) => {
          // Альфа-сортировка опций; поднятие выбранных вверх
          // выполняет shared-обёртка Select.
          const sortedOptions = useMemo(() => {
            const options = fieldConfig.options || [];
            if (fieldConfig.mode !== "multiple") {
              return options;
            }

            return [...options].sort((a, b) =>
              String(a.label).localeCompare(String(b.label), "ru", {
                sensitivity: "base",
              }),
            );
          }, [fieldConfig.options, fieldConfig.mode]);

          return (
            <SelectPD
              placeholder="Выберите"
              labelInValue
              onChange={(data) => {
                // При удалении тега SelectPD/antd подставляет в label отрендеренный
                // React-элемент (labelRender), который цикличен — react-hook-form
                // падает с Maximum call stack size exceeded на watch(). Храним в
                // форме только плоские { label, value }.
                const toPlainOption = (item: any) => {
                  const match = (fieldConfig.options || []).find(
                    (option) => String(option.value) === String(item?.value),
                  );
                  const label =
                    typeof item?.label === "string" ||
                    typeof item?.label === "number"
                      ? String(item.label)
                      : String(match?.label ?? item?.value ?? "");
                  return { label, value: item?.value };
                };

                let finalData = data;
                if (Array.isArray(data) && fieldConfig.mode === "multiple") {
                  finalData = data
                    .map(toPlainOption)
                    .sort((a, b) =>
                      String(a.label).localeCompare(String(b.label), "ru"),
                    );
                } else if (data && !Array.isArray(data)) {
                  finalData = toPlainOption(data);
                }

                onChange(finalData);
                setFastItem(null);
              }}
              value={
                fieldConfig.mode ? (Array.isArray(value) ? value : []) : value
              }
              allowClear={fieldName !== "selectFrom"}
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => {
                const labels = omittedValues
                  .map((item) => {
                    const label = item?.label;
                    if (typeof label === "string") return label;
                    if (React.isValidElement(label)) {
                      return label.props.children;
                    }
                    return item?.value || "";
                  })
                  .join(", ");
                return (
                  <Tooltip
                    styles={{ root: { pointerEvents: "none" } }}
                    title={labels}
                    variant="secondary2"
                    arrow={true}
                  >
                    <span>{`ещё ${omittedValues.length}`}</span>
                  </Tooltip>
                );
              }}
              mode={fieldConfig.mode}
              options={sortedOptions}
              showSearch={fieldName !== "selectFrom"}
              filterOption={(input, option) => {
                const opt = option as StatusOption;
                return (opt?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase());
              }}
              className={styles[fieldName]}
            />
          );
        }}
      />
    </div>
  );
};

export default Select;
